import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import BankruptcyStartDialog from '../components/landing/BankruptcyStartDialog';
import SiteFooter from '../components/landing/SiteFooter';

const HEADER_HEIGHT = 88;
const PAGE_TITLE = 'ФинРадар для арбитражных управляющих - анализ выписок по делу о банкротстве';
const META_DESCRIPTION = 'ФинРадар помогает арбитражным управляющим анализировать банковские выписки, покупки и продажи по делу о банкротстве: единая база по должнику, операции по контрагентам, зоны внимания, ЕГРЮЛ, выгрузка данных и пробный доступ на 7 дней.';
const OG_TITLE = 'ФинРадар для арбитражных управляющих - анализ выписок и сделок по делу о банкротстве';
const OG_DESCRIPTION = 'Сервис для анализа банковских выписок, покупок, продаж, сделок по делу о банкротстве. Данные по должнику, контрагенты, типы операций, зоны внимания, ЕГРЮЛ и пробный доступ на 7 дней.';
const CANONICAL_URL = 'https://finradar.brpadvice.ru/bankrotstvo/';
const META_KEYWORDS = 'анализ выписок по делу о банкротстве, анализ банковских выписок арбитражный управляющий, сервис для арбитражных управляющих, анализ движения денежных средств должника, выписки по делу о банкротстве, анализ контрагентов по выпискам, подозрительные операции банкротство, оспоримые сделки банкротство, финансовый анализ должника выписки, движение денежных средств по должнику';

const navItems = [
    { label: 'Возможности', target: '#capabilities' },
    { label: 'Тарифы', target: '#tariffs' },
    { label: 'FAQ', target: '#faq' },
];

const heroBullets = [
    '7 дней бесплатно',
    'Без банковской карты',
    'Можно начать сразу после регистрации',
];

const trustStats = [
    { value: '10+ трлн ₽', label: 'проанализированного оборота' },
    { value: '2 000+', label: 'процедур загружено в систему' },
    { value: '7 дней', label: 'бесплатный пробный доступ' },
];

const capabilityCards = [
    {
        title: 'Единая база выписок по должнику',
        icon: AccountBalanceWalletOutlinedIcon,
    },
    {
        title: 'Поконтрагентный анализ',
        icon: CorporateFareOutlinedIcon,
    },
    {
        title: 'Анализ по типам операций',
        icon: CategoryOutlinedIcon,
    },
    {
        title: 'Статусы ЕГРЮЛ',
        icon: FactCheckOutlinedIcon,
    },
    {
        title: 'Выгрузка данных',
        icon: DownloadOutlinedIcon,
    },
    {
        title: 'Зоны внимания по движению денег',
        icon: WarningAmberRoundedIcon,
    },
];

const scenarios = [
    'просматривать движение денежных средств по счетам должника в одном проекте;',
    'анализировать расчеты с конкретными контрагентами;',
    'выделять займы, цессии, лизинг, подотчет и другие типы операций;',
    'находить операции, которые могут быть подозрительными или подлежащими оспариванию;',
    'проверять контрагентов по значимым статусам ЕГРЮЛ;',
    'формировать массив данных для дальнейшего финансового анализа;',
    'использовать данные при подготовке материалов по спорным сделкам и взысканию дебиторской задолженности.',
];

const steps = [
    'Создайте проект по должнику',
    'Загрузите выписки',
    'Получите единый массив данных',
    'Используйте аналитические срезы',
    'Применяйте результаты в работе по делу',
];

const formats = [
    {
        title: 'Структурированные выписки',
        subtitle: 'Входят в тариф',
        accent: false,
    },
    {
        title: 'Text PDF и Word',
        subtitle: 'Подключается отдельно',
        price: '870 ₽ за 1 банковский формат в проекте',
        accent: true,
    },
    {
        title: 'Сканы / OCR',
        subtitle: 'По запросу',
        accent: false,
    },
];

const includedItems = [
    '1 проект = 1 должник',
    '1 пользователь',
    'доступ на 3, 6 или 12 месяцев',
    'загрузка и анализ структурированных выписок',
    'аналитика по операциям и контрагентам',
    'статусы ЕГРЮЛ по контрагентам',
    'выгрузка данных для дальнейшей работы',
];

const operationVolumeBreakdown = [
    'До 300 млн ₽ оборота - 30 000 операций',
    'До 1 млрд ₽ оборота - 40 000 операций',
    'До 5 млрд ₽ оборота - 50 000 операций',
];

const extraItems = [
    'Text PDF и Word',
    'анализ книг покупок и продаж ФНС',
    'сканы / OCR – по запросу',
];

const tariffs = [
    {
        name: 'До 300 млн ₽ оборота по счетам',
        prices: [
            { period: '3 месяца', value: '4 900 ₽' },
            { period: '6 месяцев', value: '6 900 ₽' },
            { period: '12 месяцев', value: '8 900 ₽' },
        ],
    },
    {
        name: 'До 1 млрд ₽ оборота по счетам',
        optimal: true,
        prices: [
            { period: '3 месяца', value: '5 900 ₽' },
            { period: '6 месяцев', value: '8 900 ₽' },
            { period: '12 месяцев', value: '11 900 ₽' },
        ],
    },
    {
        name: 'До 5 млрд ₽ оборота по счетам',
        prices: [
            { period: '3 месяца', value: '7 900 ₽' },
            { period: '6 месяцев', value: '10 900 ₽' },
            { period: '12 месяцев', value: '14 900 ₽' },
        ],
    },
];

const fnsAddonPrices = [
    { range: 'до 300 млн ₽', value: '2 000 ₽' },
    { range: 'до 1 млрд ₽', value: '3 000 ₽' },
    { range: 'до 5 млрд ₽', value: '4 000 ₽' },
];

const quotes = [
    'Выписки за 3 года подсвечивают, что не так и куда уходили деньги.',
    'Картина сразу становится более четкой и ясной.',
    'ФинРадар начал реально помогать в практической работе.',
    'С помощью ФинРадар уже подавались заявления в суд.',
];

const faqItems = [
    {
        question: 'Это готовый финансовый анализ должника?',
        answer: 'Нет. ФинРадар помогает собрать и структурировать банковские данные по делу, но итоговые выводы по процедуре делает арбитражный управляющий с учетом материалов дела и контекста.',
    },
    {
        question: 'Можно ли использовать сервис для поиска операций, которые могут быть подозрительными или подлежащими оспариванию?',
        answer: 'Да. Система помогает увидеть операции и контрагентов, которые требуют дополнительной проверки, но правовая квалификация и итоговая оценка остаются за управляющим.',
    },
    {
        question: 'Что входит в тариф?',
        answer: 'В базовый доступ входят проект по одному должнику, один пользователь, срок 3, 6 или 12 месяцев, работа со структурированными выписками, аналитика по операциям и контрагентам, статусы ЕГРЮЛ и выгрузка данных.',
    },
    {
        question: 'Можно ли загружать PDF-выписки?',
        answer: 'Да. Text PDF и Word подключаются отдельно: 870 ₽ за 1 банковский формат в проекте. Сначала вы загружаете выписку, затем система определяет формат и начисляет стоимость подключения. Если формат не нужен, от подключения можно отказаться.',
    },
    {
        question: 'Можно ли загружать сканы?',
        answer: 'Да, но такой формат подключается по запросу. Это отдельный сценарий обработки, поэтому он не включен в базовый тариф.',
    },
    {
        question: 'Почему доступ считается по одному проекту?',
        answer: 'Модель сервиса привязана к одной процедуре: один проект соответствует одному должнику и одному пользователю. Это упрощает расчет стоимости и управление доступом по делу.',
    },
    {
        question: 'Что входит в лимит по операциям?',
        answer: 'В базовую стоимость включено до 50 000 операций в проекте. Это лимит на объем данных, которые можно анализировать в рамках одного доступа.',
    },
    {
        question: 'Можно ли подключить дополнительные данные из ФНС?',
        answer: 'Да. При необходимости отдельно подключается анализ книг покупок и продаж ФНС. Стоимость зависит от оборота по счетам в проекте.',
    },
    {
        question: 'Есть ли пробный доступ?',
        answer: 'Да. После регистрации доступен бесплатный пробный период на 7 дней для одной процедуры, без привязки банковской карты.',
    },
];

const sectionStyles = {
    scrollMarginTop: `${HEADER_HEIGHT + 24}px`,
    py: { xs: 8, md: 11 },
};

function scrollToHash() {
    const hash = window.location.hash;
    if (!hash) return;

    const element = document.querySelector(hash);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
    window.scrollTo({ top, behavior: 'smooth' });
}

function SectionHeading({ eyebrow, title, description, align = 'left' }) {
    return (
        <Stack spacing={2} sx={{ maxWidth: 760, mb: 5, textAlign: align }}>
            {eyebrow ? (
                <Typography
                    variant="overline"
                    sx={{
                        color: '#5A6472',
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                    }}
                >
                    {eyebrow}
                </Typography>
            ) : null}
            <Typography
                variant="h2"
                sx={{
                    fontSize: { xs: 30, md: 42 },
                    lineHeight: 1.15,
                    fontWeight: 700,
                    color: '#101828',
                    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                }}
            >
                {title}
            </Typography>
            {description ? (
                <Typography
                    variant="body1"
                    sx={{
                        color: '#475467',
                        lineHeight: 1.75,
                        fontSize: { xs: 16, md: 18 },
                    }}
                >
                    {description}
                </Typography>
            ) : null}
        </Stack>
    );
}

function DashboardPreview() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 6,
                border: '1px solid #D0D5DD',
                overflow: 'hidden',
                background: 'linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)',
                boxShadow: '0 30px 80px rgba(15, 23, 42, 0.12)',
            }}
        >
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EAECF0', backgroundColor: '#FFFFFF' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, color: '#0F172A' }}>Пример рисков по выписке</Typography>
                        <Typography variant="body2" sx={{ color: '#667085' }}>
                            Интерфейсный фрагмент из системы
                        </Typography>
                    </Box>
                    <Chip label="Зоны внимания" sx={{ backgroundColor: '#FFF4E5', color: '#B54708', fontWeight: 700 }} />
                </Stack>
            </Box>
            <Box
                sx={{
                    height: { xs: 320, md: 420 },
                    overflow: 'hidden',
                    backgroundColor: '#FFFFFF',
                }}
            >
                <Box
                    component="img"
                    src="/assets/images/пример рисков по выписке.webp"
                    alt="Пример рисков по выписке в интерфейсе ФинРадар"
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                    }}
                />
            </Box>
        </Paper>
    );
}

function ValueList({ items }) {
    return (
        <Stack spacing={1.5}>
            {items.map((item) => (
                <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', mt: '2px', fontSize: 20 }} />
                    <Typography sx={{ color: '#344054', lineHeight: 1.7 }}>{item}</Typography>
                </Stack>
            ))}
        </Stack>
    );
}

function StartWorkButton({ onClick, children = 'Попробовать 7 дней бесплатно', ...props }) {
    return (
        <Button
            onClick={onClick}
            {...props}
        >
            {children}
        </Button>
    );
}

function ArbitrationLandingPage() {
    const { isAuthenticated } = useAuth();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [startDialogOpen, setStartDialogOpen] = useState(false);
    const [startDialogInitialTab, setStartDialogInitialTab] = useState('register');

    useEffect(() => {
        scrollToHash();
        window.addEventListener('hashchange', scrollToHash);

        return () => {
            window.removeEventListener('hashchange', scrollToHash);
        };
    }, []);

    const handleNavClick = (event, target) => {
        event?.preventDefault();
        setDrawerOpen(false);

        const element = document.querySelector(target);
        if (!element) return;

        const top = element.getBoundingClientRect().top + window.scrollY - HEADER_HEIGHT;
        window.scrollTo({ top, behavior: 'smooth' });

        if (window.location.hash !== target) {
            window.history.pushState(null, '', target);
        }
    };

    const handleOpenStartDialog = () => {
        setDrawerOpen(false);
        setStartDialogInitialTab('register');
        setStartDialogOpen(true);
    };

    const handleOpenLogin = () => {
        setDrawerOpen(false);

        if (isAuthenticated) {
            window.location.href = '/app/';
            return;
        }

        setStartDialogInitialTab('login');
        setStartDialogOpen(true);
    };

    return (
        <Box sx={{ backgroundColor: '#F5F7FA' }}>
            <Helmet>
                <title>{PAGE_TITLE}</title>
                <meta name="description" content={META_DESCRIPTION} />
                <meta name="keywords" content={META_KEYWORDS} />
                <meta property="og:title" content={OG_TITLE} />
                <meta property="og:description" content={OG_DESCRIPTION} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={CANONICAL_URL} />
                <link rel="canonical" href={CANONICAL_URL} />
            </Helmet>
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.92)',
                    color: '#101828',
                    borderBottom: '1px solid #EAECF0',
                    backdropFilter: 'blur(16px)',
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ minHeight: `${HEADER_HEIGHT}px`, gap: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 3,
                                    background: 'linear-gradient(135deg, #0F172A 0%, #1D4ED8 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#FFFFFF',
                                    fontWeight: 800,
                                }}
                            >
                                FR
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
                                    ФинРадар
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#667085', letterSpacing: '0.06em' }}>
                                    ДЛЯ АРБИТРАЖНЫХ УПРАВЛЯЮЩИХ
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
                        >
                            {navItems.map((item) => (
                                <Button
                                    key={item.target}
                                    component="a"
                                    href={item.target}
                                    onClick={(event) => handleNavClick(event, item.target)}
                                    sx={{ color: '#344054', px: 2 }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </Stack>

                        <StartWorkButton
                            onClick={handleOpenStartDialog}
                            variant="contained"
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                borderRadius: 999,
                                px: 3,
                                py: 1.3,
                                textTransform: 'none',
                                fontWeight: 700,
                                backgroundColor: '#155EEF',
                            }}
                        />
                        <Button
                            onClick={handleOpenLogin}
                            variant="text"
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                borderRadius: 999,
                                px: 2,
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 700,
                                color: '#344054',
                            }}
                        >
                            Войти
                        </Button>

                        <Stack direction="row" spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
                            <StartWorkButton
                                onClick={handleOpenStartDialog}
                                variant="contained"
                                sx={{
                                    borderRadius: 999,
                                    px: 2.25,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: '#155EEF',
                                }}
                            />
                            <IconButton
                                onClick={() => setDrawerOpen(true)}
                                sx={{ border: '1px solid #D0D5DD', borderRadius: 3 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Stack>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 300, p: 3 }}>
                    <Stack spacing={2}>
                        {navItems.map((item) => (
                            <ListItemButton
                                key={item.target}
                                component="a"
                                href={item.target}
                                onClick={(event) => handleNavClick(event, item.target)}
                                sx={{ borderRadius: 3 }}
                            >
                                <ListItemText primary={item.label} />
                            </ListItemButton>
                        ))}
                        <StartWorkButton
                            onClick={handleOpenStartDialog}
                            variant="contained"
                            fullWidth
                            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700, mt: 1 }}
                        />
                        <Button
                            onClick={handleOpenLogin}
                            variant="text"
                            fullWidth
                            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                        >
                            Войти
                        </Button>
                    </Stack>
                </Box>
            </Drawer>

            <Box
                component="section"
                sx={{
                    pt: { xs: 7, md: 10 },
                    pb: { xs: 8, md: 12 },
                    background:
                        'radial-gradient(circle at top right, rgba(21, 94, 239, 0.08), transparent 28%), linear-gradient(180deg, #F8FAFC 0%, #F5F7FA 100%)',
                }}
            >
                <Container maxWidth="xl">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} lg={6}>
                            <Stack spacing={4}>
                                <Box>
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: 38, md: 58 },
                                            lineHeight: 1.05,
                                            fontWeight: 800,
                                            color: '#101828',
                                            letterSpacing: '-0.03em',
                                            fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                            maxWidth: 760,
                                        }}
                                    >
                                        ФинРадар для арбитражных управляющих
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mt: 3,
                                            fontSize: { xs: 17, md: 19 },
                                            lineHeight: 1.8,
                                            color: '#475467',
                                            maxWidth: 700,
                                        }}
                                    >
                                        Анализируйте банковские выписки, покупки, продажи, сделки по делу о банкротстве в единой системе:
                                        объединяйте данные по должнику, просматривайте операции по контрагентам и
                                        типам платежей, выявляйте зоны внимания и используйте результаты в дальнейшей
                                        работе по процедуре.
                                    </Typography>
                                </Box>

                                <Grid container spacing={1.5}>
                                    {heroBullets.map((item) => (
                                        <Grid item xs={12} sm={6} key={item}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 4,
                                                    height: '100%',
                                                    backgroundColor: 'rgba(255,255,255,0.72)',
                                                }}
                                            >
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#155EEF',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography sx={{ color: '#344054', fontWeight: 600 }}>{item}</Typography>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <StartWorkButton
                                        onClick={handleOpenStartDialog}
                                        variant="contained"
                                        endIcon={<ArrowForwardRoundedIcon />}
                                        sx={{
                                            borderRadius: 999,
                                            px: 3.5,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: 16,
                                            fontWeight: 700,
                                            backgroundColor: '#155EEF',
                                        }}
                                    />
                                    <Button
                                        component="a"
                                        href="#tariffs"
                                        onClick={(event) => handleNavClick(event, '#tariffs')}
                                        variant="outlined"
                                        sx={{
                                            borderRadius: 999,
                                            px: 3.5,
                                            py: 1.5,
                                            textTransform: 'none',
                                            fontSize: 16,
                                            fontWeight: 700,
                                            borderColor: '#BFC7D4',
                                            color: '#344054',
                                        }}
                                    >
                                        Тарифы
                                    </Button>
                                </Stack>

                                <Typography sx={{ color: '#667085', fontSize: 15 }}>
                                    Пробный доступ на 7 дней по одной процедуре – бесплатно, без карты, после
                                    регистрации
                                </Typography>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <DashboardPreview />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ ...sectionStyles, py: { xs: 6, md: 8 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={2}>
                        {trustStats.map((item) => (
                            <Grid item xs={12} md={4} key={item.value}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 3.5,
                                        borderRadius: 5,
                                        backgroundColor: '#FFFFFF',
                                        height: '100%',
                                    }}
                                >
                                    <Typography sx={{ fontSize: { xs: 34, md: 42 }, fontWeight: 800, color: '#101828' }}>
                                        {item.value}
                                    </Typography>
                                    <Typography sx={{ mt: 1, color: '#667085', lineHeight: 1.7 }}>
                                        {item.label}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box id="capabilities" component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        eyebrow="ВОЗМОЖНОСТИ"
                        title="Возможности ФинРадара для арбитражного управляющего"
                        description="Сервис собирает разрозненные банковские данные в единую систему и помогает быстрее увидеть операции и контрагентов, которые требуют отдельной проверки в процедуре банкротства."
                    />
                    <Grid container spacing={2.5}>
                        {capabilityCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <Grid item xs={12} sm={6} lg={4} key={card.title}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 3,
                                            borderRadius: 5,
                                            backgroundColor: '#FFFFFF',
                                            height: '100%',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 3.5,
                                                backgroundColor: '#EEF4FF',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#155EEF',
                                                mb: 2.5,
                                            }}
                                        >
                                            <Icon />
                                        </Box>
                                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#101828', lineHeight: 1.35 }}>
                                            {card.title}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <Grid container spacing={5} alignItems="center">
                        <Grid item xs={12} lg={7}>
                            <SectionHeading title="Что можно анализировать по выпискам в деле о банкротстве" />
                            <Grid container spacing={1.75}>
                                {scenarios.map((item) => (
                                    <Grid item xs={12} md={6} key={item}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2.25,
                                                borderRadius: 4,
                                                backgroundColor: '#FFFFFF',
                                                height: '100%',
                                            }}
                                        >
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', mt: '2px' }} />
                                                <Typography sx={{ color: '#344054', lineHeight: 1.7 }}>{item}</Typography>
                                            </Stack>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3.5,
                                    borderRadius: 6,
                                    backgroundColor: '#FFFFFF',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                <Stack spacing={3}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 54,
                                                height: 54,
                                                borderRadius: 4,
                                                backgroundColor: '#EEF4FF',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#155EEF',
                                            }}
                                        >
                                            <InsightsOutlinedIcon />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, color: '#101828' }}>
                                                Аналитические срезы
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#667085' }}>
                                                Работа с движением денег по контрагентам и типам операций
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Stack spacing={1.5}>
                                        {[
                                            ['Займы', '1 240 операций'],
                                            ['Цессии', '187 операций'],
                                            ['Лизинг', '94 операции'],
                                            ['Подотчет', '326 операций'],
                                        ].map(([label, value]) => (
                                            <Box
                                                key={label}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: 2,
                                                    p: 1.75,
                                                    borderRadius: 3,
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #EAECF0',
                                                }}
                                            >
                                                <Typography sx={{ color: '#344054', fontWeight: 600 }}>{label}</Typography>
                                                <Typography sx={{ color: '#101828', fontWeight: 700 }}>{value}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={{ ...sectionStyles, pt: { xs: 2, md: 4 } }}>
                <Container maxWidth="lg">
                    <Paper
                        variant="outlined"
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: 6,
                            backgroundColor: '#F9FAFB',
                            borderColor: '#D0D5DD',
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: 26, md: 34 },
                                    fontWeight: 700,
                                    color: '#101828',
                                    lineHeight: 1.2,
                                    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                }}
                            >
                                ФинРадар помогает подготовить основу для решений по делу
                            </Typography>
                            <Typography sx={{ color: '#475467', lineHeight: 1.9, fontSize: { xs: 16, md: 18 } }}>
                                Сервис собирает и структурирует банковские данные по должнику, классифицирует
                                операции, показывает движение денежных средств в едином массиве и помогает выявить
                                контрагентов и операции, которые требуют внимания.
                            </Typography>
                            <Typography sx={{ color: '#475467', lineHeight: 1.9, fontSize: { xs: 16, md: 18 } }}>
                                Арбитражному управляющему остается оценить выявленные факты в контексте процедуры,
                                принять решение и использовать результат в дальнейшей работе по делу.
                            </Typography>
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading title="Как работать с выписками по делу в ФинРадаре" />
                    <Grid container spacing={2}>
                        {steps.map((step, index) => (
                            <Grid item xs={12} md={6} lg={2.4} key={step}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 3,
                                        borderRadius: 5,
                                        backgroundColor: '#FFFFFF',
                                        height: '100%',
                                        minHeight: 190,
                                    }}
                                >
                                    <Typography sx={{ color: '#155EEF', fontWeight: 800, mb: 2 }}>
                                        0{index + 1}
                                    </Typography>
                                    <Typography sx={{ color: '#101828', fontWeight: 700, lineHeight: 1.5 }}>
                                        {step}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading
                        title="Какие форматы выписок поддерживает ФинРадар"
                        description="В базовый тариф входит работа со сведениями о движении денежных средств в формате Excel. Если по конкретной процедуре документы предоставлены в PDF, Word или иных форматах, такие форматы подключаются отдельно."
                    />
                    <Grid container spacing={2.5}>
                        {formats.map((format) => (
                            <Grid item xs={12} md={4} key={format.title}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 3.25,
                                        borderRadius: 5,
                                        height: '100%',
                                        backgroundColor: format.accent ? '#F5F8FF' : '#FFFFFF',
                                        borderColor: format.accent ? '#B2CCFF' : '#D0D5DD',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#101828' }}>
                                        {format.title === 'Структурированные выписки' ? 'Excel: структурированные выписки' : format.title}
                                    </Typography>
                                    <Typography sx={{ mt: 1.5, color: '#667085' }}>{format.subtitle}</Typography>
                                    {format.price ? (
                                        <Typography sx={{ mt: 2.5, color: '#155EEF', fontWeight: 800, fontSize: 20 }}>
                                            {format.price}
                                        </Typography>
                                    ) : null}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading title="Что входит в тариф по делу о банкротстве" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{ p: 4, borderRadius: 6, backgroundColor: '#FFFFFF', height: '100%' }}>
                                <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#101828', mb: 3 }}>
                                    Входит
                                </Typography>
                                <ValueList items={includedItems} />
                                <Accordion
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        mt: 2,
                                        borderRadius: '16px !important',
                                        border: '1px solid #D0D5DD',
                                        backgroundColor: '#F8FAFC',
                                        '&:before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', fontSize: 20 }} />
                                            <Typography sx={{ color: '#344054', lineHeight: 1.7 }}>
                                                до 50 000 операций включено
                                            </Typography>
                                        </Stack>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ pt: 0 }}>
                                        <ValueList items={operationVolumeBreakdown} />
                                    </AccordionDetails>
                                </Accordion>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{ p: 4, borderRadius: 6, backgroundColor: '#FFFFFF', height: '100%' }}>
                                <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#101828', mb: 3 }}>
                                    Подключается отдельно
                                </Typography>
                                <ValueList items={extraItems} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Box id="tariffs" component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading title="Тарифы по делу о банкротстве" />
                    <Grid container spacing={2.5}>
                        {tariffs.map((tariff) => (
                            <Grid item xs={12} md={4} key={tariff.name}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        borderRadius: 6,
                                        backgroundColor: '#FFFFFF',
                                        borderColor: tariff.optimal ? '#98B8FF' : '#D0D5DD',
                                        boxShadow: tariff.optimal ? '0 20px 60px rgba(21, 94, 239, 0.12)' : 'none',
                                        height: '100%',
                                    }}
                                >
                                    <CardContent sx={{ p: 3.5 }}>
                                        <Stack spacing={3}>
                                            <Box>
                                                {tariff.optimal ? (
                                                    <Chip
                                                        label="Оптимальный вариант"
                                                        size="small"
                                                        sx={{
                                                            mb: 2,
                                                            backgroundColor: '#EAF2FF',
                                                            color: '#1849A9',
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                ) : null}
                                                <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#101828', lineHeight: 1.3 }}>
                                                    {tariff.name}
                                                </Typography>
                                            </Box>
                                            <Stack spacing={1.5}>
                                                {tariff.prices.map((price) => (
                                                    <Box
                                                        key={price.period}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            p: 1.75,
                                                            borderRadius: 3,
                                                            backgroundColor: '#F8FAFC',
                                                        }}
                                                    >
                                                        <Typography sx={{ color: '#475467' }}>{price.period}</Typography>
                                                        <Typography sx={{ color: '#101828', fontSize: 24, fontWeight: 800 }}>
                                                            {price.value}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Stack>
                                            <StartWorkButton
                                                onClick={handleOpenStartDialog}
                                                variant={tariff.optimal ? 'contained' : 'outlined'}
                                                fullWidth
                                                sx={{
                                                    borderRadius: 999,
                                                    py: 1.4,
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    fontSize: 16,
                                                }}
                                            />
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Paper
                        variant="outlined"
                        sx={{
                            mt: 3,
                            p: 3,
                            borderRadius: 5,
                            backgroundColor: '#FFFFFF',
                        }}
                    >
                        <Grid container spacing={1.5}>
                            {[
                                'до 50 000 операций включено',
                                '1 проект = 1 должник',
                                '1 проект = 1 пользователь',
                                'Text PDF и Word: 870 ₽ за 1 банковский формат в проекте',
                                'сканы / OCR – по запросу',
                            ].map((note) => (
                                <Grid item xs={12} md={6} lg={4} key={note}>
                                    <Stack direction="row" spacing={1.25} alignItems="center">
                                        <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', fontSize: 18 }} />
                                        <Typography sx={{ color: '#475467' }}>{note}</Typography>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>
                        <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                                mt: 2.5,
                                borderRadius: '16px !important',
                                border: '1px solid #D0D5DD',
                                backgroundColor: '#F8FAFC',
                                '&:before': { display: 'none' },
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', fontSize: 18 }} />
                                    <Typography sx={{ color: '#475467' }}>
                                        Расшифровка лимита операций по обороту
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <ValueList items={operationVolumeBreakdown} />
                            </AccordionDetails>
                        </Accordion>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={{ ...sectionStyles, pt: { xs: 2, md: 4 } }}>
                <Container maxWidth="lg">
                    <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 }, borderRadius: 6, backgroundColor: '#FFFFFF' }}>
                        <SectionHeading
                            title="Анализ книг покупок и продаж ФНС"
                            description="При необходимости к проекту можно подключить анализ книг покупок и продаж ФНС (формат Excel). Стоимость расширения зависит от оборота по счетам в проекте."
                        />
                        <Grid container spacing={2}>
                            {fnsAddonPrices.map((item) => (
                                <Grid item xs={12} md={4} key={item.range}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 4,
                                            backgroundColor: '#F8FAFC',
                                            height: '100%',
                                        }}
                                    >
                                        <Typography sx={{ color: '#475467' }}>{item.range}</Typography>
                                        <Typography sx={{ mt: 1, color: '#101828', fontSize: 28, fontWeight: 800 }}>
                                            {item.value}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="lg">
                    <Paper
                        sx={{
                            p: { xs: 4, md: 5 },
                            borderRadius: 6,
                            backgroundColor: '#EAF2FF',
                            border: '1px solid #C7DAFF',
                        }}
                    >
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Stack spacing={2.25}>
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontSize: { xs: 28, md: 38 },
                                            fontWeight: 800,
                                            color: '#101828',
                                            lineHeight: 1.15,
                                            fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                        }}
                                    >
                                        Пробный доступ на 7 дней
                                    </Typography>
                                    <Typography sx={{ color: '#344054', lineHeight: 1.85, fontSize: 17 }}>
                                        После регистрации можно бесплатно создать 1 проект по одной процедуре и
                                        оценить, как ФинРадар собирает и структурирует банковские выписки по должнику.
                                    </Typography>
                                    <Grid container spacing={1.5}>
                                        {['бесплатно', 'без банковской карты', '1 процедура', '7 дней доступа'].map((item) => (
                                            <Grid item xs={12} sm={6} key={item}>
                                                <Stack direction="row" spacing={1.25} alignItems="center">
                                                    <CheckCircleOutlineRoundedIcon sx={{ color: '#155EEF', fontSize: 20 }} />
                                                    <Typography sx={{ color: '#344054', fontWeight: 600 }}>{item}</Typography>
                                                </Stack>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <StartWorkButton
                                    onClick={handleOpenStartDialog}
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        borderRadius: 999,
                                        py: 1.6,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: 16,
                                        backgroundColor: '#155EEF',
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Container>
            </Box>

            <Box component="section" sx={sectionStyles}>
                <Container maxWidth="xl">
                    <SectionHeading title="Что говорят о работе с ФинРадаром" />
                    <Grid container spacing={2.5}>
                        {quotes.map((quote) => (
                            <Grid item xs={12} md={6} key={quote}>
                                <Paper variant="outlined" sx={{ p: 3.5, borderRadius: 5, backgroundColor: '#FFFFFF', height: '100%' }}>
                                    <Typography sx={{ color: '#101828', fontSize: 20, lineHeight: 1.6 }}>
                                        “{quote}”
                                    </Typography>
                                    <Typography sx={{ mt: 2.5, color: '#667085' }}>
                                        Из профессионального обсуждения
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <Box id="faq" component="section" sx={sectionStyles}>
                <Container maxWidth="lg">
                    <SectionHeading title="FAQ" />
                    <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
                        {faqItems.map((item) => (
                            <Accordion
                                key={item.question}
                                disableGutters
                                elevation={0}
                                sx={{
                                    borderRadius: '20px !important',
                                    border: '1px solid #D0D5DD',
                                    backgroundColor: '#FFFFFF',
                                    '&:before': { display: 'none' },
                                }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ color: '#101828', fontWeight: 700 }}>
                                        {item.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography sx={{ color: '#475467', lineHeight: 1.8 }}>
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </List>
                </Container>
            </Box>

            <Box component="section" sx={{ ...sectionStyles, pt: { xs: 2, md: 4 } }}>
                <Container maxWidth="lg">
                    <Paper
                        sx={{
                            p: { xs: 4, md: 6 },
                            borderRadius: 6,
                            backgroundColor: '#101828',
                            color: '#FFFFFF',
                        }}
                    >
                        <Stack spacing={3} alignItems={{ xs: 'flex-start', md: 'center' }} textAlign={{ md: 'center' }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: 28, md: 42 },
                                    fontWeight: 800,
                                    lineHeight: 1.12,
                                    color: '#FFFFFF',
                                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
                                    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
                                    maxWidth: 820,
                                }}
                            >
                                Начните работу с выписками по делу в ФинРадаре
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.94)', maxWidth: 760, lineHeight: 1.8, fontSize: 17, fontWeight: 500 }}>
                                Создайте проект по должнику, загрузите выписки и получите структурированную картину
                                движения денежных средств в одной системе.
                            </Typography>
                            <StartWorkButton
                                onClick={handleOpenStartDialog}
                                variant="contained"
                                sx={{
                                    borderRadius: 999,
                                    px: 3.5,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: 16,
                                    backgroundColor: '#FFFFFF',
                                    color: '#101828',
                                    '&:hover': { backgroundColor: '#E5E7EB' },
                                }}
                            />
                        </Stack>
                    </Paper>
                </Container>
            </Box>

            <BankruptcyStartDialog
                open={startDialogOpen}
                onClose={() => setStartDialogOpen(false)}
                isAuthenticated={isAuthenticated}
                initialAuthTab={startDialogInitialTab}
            />

            <SiteFooter onRegisterClick={handleOpenStartDialog} />
        </Box>
    );
}

export default ArbitrationLandingPage;
